import React from 'react'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid'
import classnames from 'classnames'
import { Tooltip } from 'flowbite-react'
import type { SortDir } from './tariffRateSort'

export const STATUS_GLOSSARY: Record<string, string> = {
    WAITING_FOR_APPROVAL: 'Drafted or ingested rateset, not yet approved for billing.',
    PENDING: 'Drafted or ingested rateset, not yet approved for billing.',
    APPROVED: 'Approved for billing for the rateset date window.',
    ACTIVE: 'Approved for billing for the rateset date window.',
    ARCHIVED: 'Withdrawn or retired. Prefer not to assign.',
    INACTIVE: 'Withdrawn or retired. Prefer not to assign.',
    Pending: 'Drafted or ingested rateset, not yet approved for billing.',
    Active: 'Approved for billing for the rateset date window.',
    Inactive: 'Withdrawn or retired. Prefer not to assign.',
}

export const PCIA_YEAR_HINT =
    'Power Charge Indifference Adjustment. The departing-load year this charge applies to. One rateset can have several PCIA years.'

export const RATE_GROUP_GLOSSARY: Record<string, string> = {
    SERVICE: 'Energy / volumetric charges.',
    DEMAND: 'Demand (kW) charges.',
    GENERATION: 'Generation / supply component.',
    DELIVERY: 'Delivery / distribution component.',
    NBC: 'Non-bypassable charges.',
    BUNDLED_FEE: 'Bundled fee / fixed charges.',
    PDP: 'Peak Day Pricing',
    NONE: 'Not assigned to a billing rollup group. Common on imported or older rows.',
}

export const RATE_COLUMN_GLOSSARY: Record<string, string> = {
    Name: 'Charge name as stored on the tariff rate row.',
    Rate: 'Unit price for this charge.',
    Type: 'Rate charge type (e.g. CHARGE, ADJUSTMENT, MAX).',
    Category: 'Billing category within the utility schedule.',
    Season: 'Season window when the rate applies (e.g. SUMMER, WINTER).',
    TOU: 'Time-of-use period (e.g. PEAK, OFF_PEAK).',
    Group: 'Rate group used for billing rollup and filtering.',
    Effective: 'Date the rate becomes effective.',
    End: 'Date the rate stops applying (blank means open-ended).',
    Vintage: PCIA_YEAR_HINT,
    'PCIA year': PCIA_YEAR_HINT,
    Notes: 'Ops notes on this rate row.',
    ID: 'Rate row cuid.',
    Total: 'Generation + delivery + NBC for this season and TOU.',
    Generation: 'TOU generation rate plus non-TOU generation adders. ADJUSTMENT rows subtract.',
    Delivery: 'TOU delivery rate plus non-TOU delivery adders. ADJUSTMENT rows subtract.',
    NBC: 'Sum of non-bypassable charges. ADJUSTMENT rows subtract.',
    'Delivery+NBC': 'Delivery plus NBC, used when validating the delivery-side total.',
    'Delivery+NBC-WF': 'Delivery plus NBC excluding charges whose name matches wildfire fund.',
    'Generation Delta': 'Change vs the previous rateset for the same season, TOU, and vintage.',
    'Delivery Delta': 'Change vs the previous rateset for the same season, TOU, and vintage.',
    'NBC Delta': 'Change vs the previous rateset for the same season, TOU, and vintage.',
}

const tooltipClass =
    'max-w-sm whitespace-normal break-words text-xs font-normal normal-case z-[200] pointer-events-none select-none bg-slate-700 dark:bg-slate-950 border border-slate-600 dark:border-slate-800'

const tooltipTheme = {
    arrow: {
        style: {
            dark: 'bg-slate-700 dark:bg-slate-950',
            light: 'bg-slate-700 dark:bg-slate-950',
            auto: 'bg-slate-700 dark:bg-slate-950',
        },
    },
}

export const FieldTooltip: React.FC<{
    content: string
    children: React.ReactNode
    /** Wrapper around children; default keeps glossary chips/labels inline. */
    className?: string
}> = ({ content, children, className = 'inline-flex items-center' }) => (
    <Tooltip content={content} className={tooltipClass} theme={tooltipTheme} placement="top">
        <span className={className}>{children}</span>
    </Tooltip>
)

/** Column header with Flowbite tooltip (no info icon; sentence-case tip). */
export const ColumnHeaderHint: React.FC<{
    label: string
    hint?: string
}> = ({ label, hint }) => {
    const content = hint ?? RATE_COLUMN_GLOSSARY[label]
    if (content == null) return <>{label}</>
    return (
        <FieldTooltip content={content}>
            <span className="border-b border-dotted border-slate-400/70">{label}</span>
        </FieldTooltip>
    )
}

export const SortableColumnHeader: React.FC<{
    label: string
    sortKey: string
    activeKey: string | null
    dir: SortDir | null
    onSort: (key: string) => void
    hint?: string
    className?: string
}> = ({ label, sortKey, activeKey, dir, onSort, hint, className }) => {
    const active = activeKey === sortKey
    const ariaSort = active ? (dir === 'DESC' ? 'descending' : 'ascending') : 'none'
    return (
        <th className={className} aria-sort={ariaSort}>
            <button
                type="button"
                onClick={() => onSort(sortKey)}
                className="group/sort inline-flex items-center gap-0.5 text-left"
                aria-label={`Sort by ${label}`}
            >
                <ColumnHeaderHint label={label} hint={hint} />
                <span
                    className={classnames(
                        'inline-flex h-3.5 w-3.5 shrink-0',
                        active
                            ? 'text-slate-600 dark:text-slate-200'
                            : 'text-slate-400 opacity-0 group-hover/sort:opacity-50 dark:text-slate-500'
                    )}
                    aria-hidden="true"
                >
                    {active && dir === 'DESC' ? (
                        <ChevronDownIcon className="h-3.5 w-3.5" />
                    ) : (
                        <ChevronUpIcon className="h-3.5 w-3.5" />
                    )}
                </span>
            </button>
        </th>
    )
}

export const CountBadge: React.FC<{
    count: number
    className?: string
    tone?: 'indigo' | 'slate'
}> = ({ count, className = '', tone = 'indigo' }) => (
    <span
        className={
            tone === 'indigo'
                ? `inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-indigo-600 px-1.5 text-[11px] font-semibold tabular-nums text-white dark:bg-indigo-500 ${className}`
                : `inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-slate-500/20 px-1.5 text-[11px] font-semibold tabular-nums text-slate-700 dark:bg-slate-900/40 dark:text-slate-200 ${className}`
        }
    >
        {count.toLocaleString()}
    </span>
)
